<template>
    <gmap-map
        ref="map"
        :center="defaultCenter"
        :zoom="zoom"
        :options="options"
        class="input-map-frame"
    >
        <gmap-marker
            v-for="(m, index) in markers"
            :key="index"
            :position="m.position"
            :clickable="true"
            :draggable="false"
            :title="m.title"
            :icon="m.icon"
            :label="m.label"
            :z-index="m.zindex"
        />
    </gmap-map>
</template>

<script>
import { mapGetters } from 'vuex'
import configType from '@/store/type/configType'
import GoogleMapAddressMixin from '../InputMixins/GoogleMapAddressMixin'
import { stuartCourierMarkerIcon } from '@/enum/stuartTransportType'

// The pickup / dropoff markers are the ordit-uk-web2 Pin (round head + pointer + shadow,
// 36x42): the location sits just under the pointer tip, not at the image bottom where
// Google anchors by default (same spot as web2's Mapbox marker offset).
const PIN_ANCHOR = { x: 18, y: 36 }

// The courier marker is a 34px disc (public/img/map-courier.svg and stuartCourierMarkerIcon);
// its name label is centred this many px under the disc's bottom edge.
const COURIER_MARKER_SIZE = 34
const COURIER_LABEL_GAP = 12
// Google renders a MarkerLabel inside the map's own DOM (font and colour come from the
// MarkerLabel fields, the class only adds the pill — styled globally below).
const MARKER_LABEL_STYLE = {
    className: 'map-marker-label',
    fontSize: '11px',
    fontWeight: '500',
    color: '#333333',
}

export default {
    mixins: [GoogleMapAddressMixin],
    props: {
        zoom: {
            type: Number,
            default: 16,
        },
        draggable: {
            type: Boolean,
            default: true,
        },
        options: {
            type: Object,
            default() {
                return {
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                    maxZoom: 16,
                }
            },
        },
        updateAddressDetails: {
            type: Boolean,
        },
        name: {
            type: String,
            default: '',
        },
        addresses: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            markers: [],
            isReady: false,
        }
    },
    computed: {
        ...mapGetters({
            defaultCenter: configType.getters.GOOGLE_MAPS_DEFAULT_CENTER,
        }),
        point() {
            return {
                lat:
                    this.latitude == null
                        ? this.defaultCenter.lat
                        : this.latitude,
                lng:
                    this.longitude == null
                        ? this.defaultCenter.lng
                        : this.longitude,
            }
        },
        hasAddress() {
            return (
                this.addresses !== null &&
                this.addresses.length > 0 &&
                this.addresses[0].address !== null &&
                this.addresses[0].address !== ''
            )
        },
    },
    watch: {
        isReady(value) {
            if (value && this.hasAddress) this.getMarkers()
        },

        markers(markers) {
            if (this.isReady && markers?.length > 1) this.setCenterMap()
        },

        addresses() {
            if (this.isReady && this.hasAddress) this.getMarkers()
        },
    },
    async mounted() {
        await this.$gmapApiPromiseLazy()
        this.$geocoder = new google.maps.Geocoder()
        this.isReady = true
    },
    methods: {
        markerDragEnd(event) {
            this.updateAddress({
                lat: event.latLng.lat(),
                lng: event.latLng.lng(),
            })
        },
        setCenterMap() {
            const bounds = new google.maps.LatLngBounds()
            const markers = this.markers.filter((marker) => {
                return marker.focused || marker.focused === undefined
            })
            markers.sort((a, b) => a.position?.lng - b.position?.lng)
            markers.forEach((marker) => {
                bounds.extend(marker.position)
            })

            this.$refs.map.$mapPromise.then((map) => {
                map.setCenter(bounds.getCenter())
                map.fitBounds(bounds)
                map.setZoom(map.getZoom())
            })
        },
        setCenter(position) {
            this.$refs.map.$mapPromise.then((map) => {
                map.setCenter(position)
            })
        },
        getMarkers() {
            if (this.isReady) {
                const addresses = this.addresses
                this.markers = []
                addresses.forEach((address) => {
                    if (address.lat && address.lng) {
                        this.pushMarker(address)
                    } else if (address.address) {
                        this.$geocoder
                            .geocode(
                                { address: address.address },
                                (results, status) => {
                                    if (
                                        status == google.maps.GeocoderStatus.OK
                                    ) {
                                        address.lat = results[0].geometry.location.lat()
                                        address.lng = results[0].geometry.location.lng()
                                    }
                                }
                            )
                            .then(() => {
                                this.pushMarker(address)
                            })
                            .catch((reason) => {
                                throw reason
                            })
                    }
                })
            }
        },
        pushMarker(address) {
            this.markers.push({
                position: {
                    lat: address.lat,
                    lng: address.lng,
                    address: address.address,
                },
                title: address.title,
                zindex: address.zindex,
                focused: address.focused,
                label: address.label
                    ? { text: address.label, ...MARKER_LABEL_STYLE }
                    : undefined,
                icon: this.getSiteIcon(address['icon'], address), // if you want to show different as per the condition.
            })
        },
        pinIcon(url) {
            return {
                url,
                anchor: new google.maps.Point(PIN_ANCHOR.x, PIN_ANCHOR.y),
            }
        },
        getSiteIcon(icon, address = {}) {
            switch (icon) {
                case 'restaurant':
                    return this.pinIcon('/img/map-pickup-v2.svg')

                case 'dropoff':
                    return this.pinIcon('/img/map-dropoff.svg')

                case 'courier': {
                    // only Stuart fills the transport type; other providers keep the generic marker
                    const url = address.transportType?.trim()
                        ? stuartCourierMarkerIcon(address.transportType.trim())
                        : '/img/map-courier.svg'
                    // a labelled courier needs an Icon object for labelOrigin (Google keeps its
                    // default bottom-centre anchor, so the disc sits where it did with the bare url)
                    return address.label
                        ? {
                              url,
                              labelOrigin: new google.maps.Point(
                                  COURIER_MARKER_SIZE / 2,
                                  COURIER_MARKER_SIZE + COURIER_LABEL_GAP
                              ),
                          }
                        : url
                }

                case null:
                    return null

                default:
                    return this.pinIcon('/img/map-pickup-v2.svg')
            }
        },
    },
}
</script>

<style lang="scss">
// Google Maps puts the marker label into the map's own DOM, outside this component's scope
.map-marker-label {
    padding: 1px 6px;
    border-radius: 9px;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    line-height: 16px;
    white-space: nowrap;
}
</style>
